import { memo, useEffect, useRef, useState } from 'react';
import { Alert, Button, Snackbar } from '@mui/material';
import GetAppIcon from '@mui/icons-material/GetApp';

const DISMISSED_KEY = 'pwa-install-dismissed';

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
  prompt(): Promise<void>;
}

export const PwaInstallBanner = memo(() => {
  const [open, setOpen] = useState(false);
  const deferredPrompt = useRef<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    if (localStorage.getItem(DISMISSED_KEY)) return;

    const handlePrompt = (e: Event) => {
      e.preventDefault();
      deferredPrompt.current = e as BeforeInstallPromptEvent;
      setOpen(true);
    };

    const handleInstalled = () => {
      setOpen(false);
      deferredPrompt.current = null;
    };

    window.addEventListener('beforeinstallprompt', handlePrompt);
    window.addEventListener('appinstalled', handleInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handlePrompt);
      window.removeEventListener('appinstalled', handleInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt.current) return;
    await deferredPrompt.current.prompt();
    const { outcome } = await deferredPrompt.current.userChoice;
    if (outcome === 'accepted') {
      localStorage.setItem(DISMISSED_KEY, '1');
    }
    // If the user dismissed the native prompt, the banner closes for this session.
    // Chrome will re-fire beforeinstallprompt on a future visit after a cooldown.
    deferredPrompt.current = null;
    setOpen(false);
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISSED_KEY, '1');
    setOpen(false);
    deferredPrompt.current = null;
  };

  return (
    <Snackbar
      open={open}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert
        severity="info"
        variant="filled"
        onClose={handleDismiss}
        action={
          <Button
            color="inherit"
            size="small"
            startIcon={<GetAppIcon />}
            onClick={handleInstall}
            sx={{ fontWeight: 600 }}
          >
            Install
          </Button>
        }
        sx={{ width: '100%', alignItems: 'center' }}
      >
        Add SquareUp to your home screen for the best experience.
      </Alert>
    </Snackbar>
  );
});

PwaInstallBanner.displayName = 'PwaInstallBanner';
